import React, { useEffect, useState, useCallback } from 'react';
import { Copy, QrCode, TrendingUp, Wallet, Users, Check, Eye, EyeOff } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { getDealerReferral, getDealerStats } from '@/lib/api';

const DashboardPage = () => {
  const { dealer } = useAuth();
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    withdrawable: 0
  });
  const [copied, setCopied] = useState(false);
  const [showStats, setShowStats] = useState(false);

  const fetchReferralLink = useCallback(async () => {
    if (!dealer) return;
    try {
      const res = await getDealerReferral(dealer.id);
      setReferralLink(res.data.referralLink);
    } catch (error) {
      console.error('Error fetching referral link:', error);
    }
  }, [dealer]);

  const fetchStats = useCallback(async () => {
    if (!dealer) return;
    try {
      const res = await getDealerStats(dealer.id);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [dealer]);

  useEffect(() => {
    if (dealer) {
      fetchReferralLink();
      fetchStats();
    }
  }, [dealer, fetchReferralLink, fetchStats]);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="dashboard-title">Dashboard</h1>
            <p className="text-slate-600 mt-1">Welcome back, {dealer?.name}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowStats(!showStats)}
            data-testid="toggle-stats-button"
            className="flex items-center gap-2"
          >
            {showStats ? (
              <>
                <EyeOff className="h-4 w-4" />
                Hide Stats
              </>
            ) : (
              <>
                <Eye className="h-4 w-4" />
                Show Stats
              </>
            )}
          </Button>
        </div>

        {/* Referral Section - MOVED TO TOP */}
        {dealer?.status === 'approved' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Referral Link */}
            <Card data-testid="referral-link-card">
              <CardHeader>
                <CardTitle>Your Referral Link</CardTitle>
                <CardDescription>Share this link with tenants to earn commission</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <div className="flex-1 px-4 py-3 bg-slate-50 rounded-lg border border-slate-200 font-mono text-sm text-slate-700 truncate">
                    {referralLink}
                  </div>
                  <Button 
                    onClick={handleCopyLink}
                    data-testid="copy-link-button"
                    variant={copied ? "default" : "outline"}
                    className="shrink-0"
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Share this link via WhatsApp, SMS, or social media to start earning
                </p>
              </CardContent>
            </Card>

            {/* QR Code */}
            <Card data-testid="qr-code-card">
              <CardHeader>
                <CardTitle>QR Code</CardTitle>
                <CardDescription>Let tenants scan this code to use your referral</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center">
                {referralLink && (
                  <div className="bg-white p-6 rounded-xl border-2 border-slate-200">
                    <QRCodeSVG 
                      value={referralLink} 
                      size={180}
                      level="H"
                      includeMargin={false}
                    />
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-4 text-center">
                  Download and share this QR code
                </p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <Card className="border-2 border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="text-center space-y-3">
                <div className="mx-auto w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
                  <QrCode className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-lg font-semibold text-yellow-900">Account Pending Approval</h3>
                <p className="text-sm text-yellow-800">
                  Your account is currently under review. You will receive your referral link once approved by the admin.
                </p>
                <p className="text-xs text-yellow-700">
                  Status: <span className="font-semibold">{dealer?.status || 'Pending'}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats Cards - NOW BELOW REFERRAL */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="total-referrals-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Referrals</CardTitle>
              <Users className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {showStats ? stats.totalReferrals : '•••'}
              </div>
              <p className="text-xs text-slate-500 mt-1">Active referrals</p>
            </CardContent>
          </Card>

          <Card data-testid="total-earnings-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Total Earnings</CardTitle>
              <TrendingUp className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {showStats ? `₹${stats.totalEarnings.toLocaleString()}` : '₹•••'}
              </div>
              <p className="text-xs text-slate-500 mt-1">Lifetime commission</p>
            </CardContent>
          </Card>

          <Card data-testid="withdrawable-balance-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Withdrawable</CardTitle>
              <Wallet className="h-4 w-4 text-slate-400" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {showStats ? `₹${stats.withdrawable.toLocaleString()}` : '₹•••'}
              </div>
              <p className="text-xs text-slate-500 mt-1">Available balance</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
